import React, { useEffect } from "react";
import { useImmer } from "use-immer";

import {
  IgPaper,
  IgSelect,
  IgSwitch,
  IgTypography,
} from "@intelligems/ig-theme";
import { StripeApiGetAllProductsResponseBodyType } from "@intelligems/ig-types";
import { Grid, MenuItem, useTheme } from "@mui/material";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { organizationsKeys } from "@src/api/queryKeys";
import { useStripeApi } from "@src/api/stripe/stripe-api";
import { useIsIntelligems } from "@src/api/user-auths/is-intelligems";
import { useDispatch, useSelector } from "@src/store";
import {
  selectedViewAsMode,
  selectedViewAsProductId,
  setViewAsProductId,
} from "@src/store/slices/appSettings";
import { useQueryClient } from "@tanstack/react-query";

export const ViewAs = () => {
  const theme = useTheme();
  const isIntelligems = useIsIntelligems();
  const org = useGetOrg();

  const viewAsModeEnabled = useSelector(selectedViewAsMode);
  const viewAsProductId = useSelector(selectedViewAsProductId);

  const [enabled, setEnabled] = useImmer(!!viewAsProductId);

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const orgs = [{ id: org.data.org.id, shopId: org.data.org.shopId! }];

  const products = useStripeApi<StripeApiGetAllProductsResponseBodyType>(
    {
      action: "getAllProducts",
      orgs,
    },
    enabled
  );

  useEffect(() => {
    if (!viewAsProductId) {
      setViewAsProductId("live");
    }
  }, []);

  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: organizationsKeys.all });
  }, [viewAsModeEnabled, viewAsProductId]);

  if (!isIntelligems || !viewAsModeEnabled) {
    return null;
  }

  return (
    <IgPaper
      sx={{
        position: "fixed",
        bottom: 0,
        background: theme.igPrimitives.gray.A200,
        left: 259,
        opacity: "20%",
        "&:hover, &:focus": {
          opacity: "100%",
        },
        borderRadius: "8px 8px 0px 0px",
        width: "300px",
        minHeight: "40px",
        padding: "5px 10px 0px 10px",
      }}
    >
      <Grid
        container
        item
        alignItems={"center"}
        justifyContent={"center"}
        columnSpacing={2}
        sx={{ height: "46px" }}
      >
        <Grid item>
          <IgSwitch
            size="small"
            checked={enabled}
            onChange={(e) => {
              setEnabled(e.target.checked);
              if (!e.target.checked) {
                dispatch(setViewAsProductId(undefined));
              }
            }}
          />
        </Grid>
        <Grid item>
          {enabled ? (
            <IgSelect<string>
              sx={{
                zIndex: 99999,
                ".MuiSvgIcon-root": { color: "white" },
                backgroundColor: "transparent",
                color: "white",
                fontSize: "14px",
                width: "180px",
              }}
              size={"small"}
              variant={"outlined"}
              value={viewAsProductId || "live"}
              onChange={(event) =>
                dispatch(setViewAsProductId(event.target.value))
              }
            >
              <MenuItem
                sx={{ zIndex: 9999999, fontSize: "14px" }}
                key={"live"}
                value={"live"}
              >
                Live Product
              </MenuItem>
              {products.data?.products?.map((product) => (
                <MenuItem
                  sx={{ zIndex: 9999999, fontSize: "14px" }}
                  key={product.id}
                  value={product.id}
                >
                  {product.name}
                </MenuItem>
              ))}
            </IgSelect>
          ) : (
            <IgTypography variant={"body2"} sx={{ color: "white" }}>
              View As Disabled
            </IgTypography>
          )}
        </Grid>
      </Grid>
    </IgPaper>
  );
};
